import React, { useState, useEffect } from 'react';
import { Route, Switch, Redirect, useRouteMatch } from 'react-router-dom';
import { ConnectedRouter } from 'connected-react-router';
import Loadable from 'react-loadable';
import Loader from 'components/layout-components/Loader';
import IndexLayout from 'layouts';
import NotFoundPage from 'pages/404';
import MainLayout from 'layouts/Main';
import PublicLayout from 'layouts/Public';
import moment from 'moment-timezone';
import { useSelector } from 'react-redux';
import { getCompany, getUser } from 'redux/selectors';
import { v4 as uuidv4 } from 'uuid';

import RedirectComponent from './components/shared/RedirectComponent';

const loadable = loader =>
  Loadable({
    loader,
    delay: false,
    loading: () => <Loader />,
  });

const userRoutes = [
  {
    path: 'login',
    component: loadable(() => import('pages/public/login')),
    exact: true,
  },
  {
    path: 'forgot',
    component: loadable(() => import('pages/public/forgot')),
    exact: true,
  },
  {
    path: 'reset-password',
    component: loadable(() => import('pages/public/reset')),
    exact: true,
  },
];

const privateRoutes = [
  // Profile
  {
    path: '/profile/manage',
    component: loadable(() => import('pages/profile/manage')),
  },
  {
    path: '/profile/duty',
    component: loadable(() => import('pages/profile/duty')),
  },

  // Data
  {
    path: '/data/airports',
    component: loadable(() => import('pages/data/airports/AirportsPage')),
    exact: true,
  },
  {
    path: '/data/organization/users',
    component: loadable(() => import('pages/data/organization/users')),
    exact: true,
  },
  {
    path: '/data/organization/aircraft',
    component: loadable(() => import('pages/data/organization/aircraft')),
    exact: true,
  },
  {
    path: '/data/organization/fleet-overview',
    component: loadable(() => import('pages/data/organization/fleet-overview')),
    exact: true,
  },
  {
    path: '/data/organization/aerochex',
    component: loadable(() => import('pages/data/organization/aerochex')),
    exact: true,
  },
  {
    path: '/data/organization/logs',
    component: loadable(() => import('pages/data/organization/logs')),
    exact: true,
  },
  {
    path: '/data/organization/parts',
    component: loadable(() => import('pages/data/organization/parts')),
    exact: true,
  },
  {
    path: '/data/organization/parts/:id',
    component: loadable(() => import('pages/data/organization/part-details')),
    exact: true,
  },
  {
    path: '/data/organization/flights',
    component: loadable(() => import('pages/data/organization/flights')),
    exact: true,
  },
  {
    path: '/data/organization/duty-times',
    component: loadable(() => import('pages/data/organization/duty-times')),
    exact: true,
  },
  {
    path: '/data/organization/crew-compliance',
    component: loadable(() => import('pages/data/organization/crew-compliance')),
    exact: false,
  },
  {
    path: '/data/users/:id',
    component: loadable(() => import('pages/data/user-details')),
    exact: true,
  },
  {
    path: '/data/aircraft/logs/:id',
    component: loadable(() => import('pages/data/aircraft-log/AircraftLogPage')),
    exact: true,
  },
  {
    path: '/data/aircraft/:id',
    component: loadable(() => import('pages/data/aircraft-details/AircraftDetailsPage')),
    exact: true,
  },
  {
    path: '/data/organization/crew-scheduling',
    component: loadable(() => import('pages/data/organization/crew-scheduling')),
    exact: true,
  },
  {
    path: '/data/organization/crew-scheduling-v3',
    component: loadable(() => import('pages/data/organization/crew-scheduling-v3')),
    exact: true,
  },
  {
    path: '/data/organization/crew-scheduling-mobile',
    component: loadable(() => import('pages/data/organization/crew-scheduling-mobile')),
    exact: true,
  },
  {
    path: '/data/organization/dashboard',
    component: () => <Redirect to="/data/organization/metrics-dashboard" />,
    exact: true,
  },
  {
    path: '/data/organization/sales-dashboard',
    component: loadable(() => import('pages/data/organization/sales-dashboard')),
    exact: true,
  },
  {
    path: '/data/organization/minimums-dashboard',
    component: loadable(() => import('pages/data/organization/minimums-dashboard')),
    exact: true,
  },
  {
    path: '/data/organization/metrics-dashboard',
    component: loadable(() => import('pages/data/organization/metrics-dashboard')),
    exact: true,
  },
  {
    path: '/data/organization/regulations',
    component: loadable(() => import('pages/data/organization/regulations')),
    exact: true,
  },
  {
    path: '/data/organization/reports',
    component: loadable(() => import('pages/data/organization/reports')),
    exact: true,
  },
  {
    path: '/data/organization/settings',
    component: loadable(() => import('pages/data/organization/settings')),
    exact: true,
  },
  {
    path: '/data/organization/settings/aerostudies-users',
    component: loadable(() => import('pages/data/organization/settings/aerostudies-users')),
    exact: true,
  },
  {
    path: '/data/organization/settings/nabu-users',
    component: loadable(() => import('pages/data/organization/settings/nabu-users')),
    exact: true,
  },
  {
    path: '/data/organization/flight-watch',
    component: loadable(() => import('pages/data/organization/flight-watch')),
    exact: true,
  },
  {
    path: '/data/organization/pilot-status',
    component: loadable(() => import('pages/data/organization/pilot-status')),
    exact: true,
  },
  {
    path: '/data/organization/company-library',
    component: loadable(() => import('pages/data/organization/company-library')),
    exact: true,
  },
  {
    path: '/data/organization/tracked-items',
    component: loadable(() => import('pages/data/organization/tracked-items')),
    exact: true,
  },
  {
    path: '/data/organization/custom-forms',
    component: loadable(() => import('pages/data/organization/custom-forms')),
    exact: true,
  },
  {
    path: '/data/organization/company-clients',
    component: loadable(() => import('pages/data/organization/company-clients')),
    exact: true,
  },
  {
    path: '/data/organization/company-clients/:companyClient',
    component: loadable(() =>
      import('pages/data/organization/company-clients/components/CompanyClientPage'),
    ),
    exact: true,
  },
  {
    path: '/data/organization/company-clients/:companyClient/contracts/:contractId',
    component: loadable(() =>
      import('pages/data/organization/company-clients/components/ClientContractPage'),
    ),
    exact: true,
  },
  {
    path: '/data/organization/invoices',
    component: loadable(() => import('pages/data/organization/invoices')),
    exact: true,
  },
  {
    path: '/data/organization/invoices/:invoiceId',
    component: loadable(() => import('pages/data/organization/invoices/components/InvoicePage')),
    exact: true,
  },
  {
    path: '/data/organization/groups',
    component: loadable(() => import('pages/data/organization/groups')),
    exact: true,
  },
  {
    path: '/data/organization/air-bills',
    component: loadable(() => import('pages/data/organization/air-bills')),
    exact: true,
  },
  {
    path: '/data/organization/air-bills/:airBillId',
    component: loadable(() => import('pages/data/organization/air-bills/components/AirBillPage')),
    exact: true,
  },
  {
    path: '/data/organization/published-schedule',
    component: loadable(() => import('pages/data/organization/published-schedule')),
    exact: true,
  },
  {
    path: '/data/organization/bookings',
    component: loadable(() => import('pages/data/organization/bookings')),
    exact: true,
  },
  {
    path: '/data/organization/outbox-monitor',
    component: loadable(() => import('pages/data/organization/outbox-monitor')),
    exact: true,
  },
  {
    path: '/data/organization/manifest/:page+',
    component: loadable(() => import('pages/data/organization/manifest')),
  },
  // {
  //   path: '/settings/timezone',
  //   component: loadable(() => import('pages/settings/timezone')),
  //   exact: true,
  // },

  // // Admin
  // {
  //   path: '/admin',
  //   component: loadable(() => import('pages/admin')),
  //   exact: true,
  // },
];

const SignAirbill = loadable(() => import('pages/public/sign-airbill'));
const AeroStudiesReturn = loadable(() => import('pages/public/aerostudies-return'));

export default function Router({ history }) {
  const company = useSelector(getCompany);
  const user = useSelector(getUser);

  const [companySettings, setCompanySettings] = useState(null);
  const [userSettings, setUserSettings] = useState(null);

  useEffect(() => {
    if (company && user) {
      if (company?.Settings) {
        setCompanySettings({
          CanMemberChangeTimezone: company.Settings.CanMemberChangeTimezone,
          CompanyDefaultTimezone: company.Settings.CompanyDefaultTimezone,
        });
      }
      if (user?.Profile.Metadata) {
        setUserSettings({
          SelectedTimeZone: user.Profile.Metadata.SelectedTimeZone
            ? user.Profile.Metadata.SelectedTimeZone
            : '',
        });
      }
    }
  }, [company, user]);

  useEffect(() => {
    if (companySettings?.CanMemberChangeTimezone && userSettings?.SelectedTimeZone.length > 0) {
      moment.tz.setDefault(userSettings.SelectedTimeZone);
    } else if (companySettings?.CompanyDefaultTimezone) {
      moment.tz.setDefault(companySettings.CompanyDefaultTimezone);
    } else {
      const defaultZone = moment.tz.zone(moment.tz.guess()).name;
      moment.tz.setDefault(defaultZone);
    }
  }, [userSettings, companySettings]);

  const timezoneInUtc = company?.Settings?.InputTimeInUTC;

  if (timezoneInUtc) {
    // the default is local time zone so we only reset the default when we want UTC
    // moment.tz.setDefault('UTC');
  }

  return (
    <ConnectedRouter history={history}>
      <RedirectComponent />
      <Switch>
        <Route exact path="/airbill/:airBillId/signature/:token" component={SignAirbill} />
        <Route exact path="/aerostudies/return" component={AeroStudiesReturn} />
        <Route>
          <IndexLayout>
            <Switch>
              <Route
                exact
                path="/"
                component={() => <Redirect to="/data/organization/aircraft" />}
              />
              <Redirect
                from="/data/organization/crew-scheduling-v2"
                to="/data/organization/crew-scheduling-v3"
              />
              <Route path="/user">
                <PublicRoutes />
              </Route>
              <Route path="/">
                <IndexLayout>
                  <MainLayout>
                    <Switch>
                      {privateRoutes.map(route => (
                        <Route
                          path={route.path}
                          component={route.component}
                          key={route.path}
                          exact={route.exact}
                        />
                      ))}
                    </Switch>
                  </MainLayout>
                </IndexLayout>
              </Route>
              <Route component={NotFoundPage} />
            </Switch>
          </IndexLayout>
        </Route>
      </Switch>
    </ConnectedRouter>
  );
}

function PublicRoutes() {
  const { path } = useRouteMatch();

  return (
    <PublicLayout>
      <Switch>
        {userRoutes.map(route => (
          <Route
            path={`${path}/${route.path}`}
            component={route.component}
            exact={route.exact}
            key={uuidv4()}
          />
        ))}
      </Switch>
    </PublicLayout>
  );
}
